import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Orb } from "@/components/Orb";

export default function Landing() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && user) navigate("/dashboard", { replace: true });
  }, [user, loading, navigate]);

  if (loading || user) {
    return (
      <div className="bf-noise flex min-h-screen items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-bf-gold/30 border-t-bf-gold" />
      </div>
    );
  }

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="bf-noise relative flex min-h-screen flex-col overflow-hidden">
      <Orb stage={3} total={8} size={520} />

      <div className="mx-auto flex w-full max-w-2xl flex-1 flex-col justify-center px-6 py-16 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: "easeOut" }}
        >
          <div className="mb-8 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs uppercase tracking-[0.18em] text-bf-muted backdrop-blur-md">
            <Sparkles className="h-3.5 w-3.5 text-bf-gold" />
            Bliss Factory
          </div>

          <h1
            data-testid="landing-title"
            className="font-serif text-4xl leading-[1.1] tracking-tight text-bf-text sm:text-5xl lg:text-6xl"
          >
            A quiet conversation to help you{" "}
            <span className="bf-gradient-text">come alive</span>.
          </h1>

          <p className="mt-7 max-w-xl font-sans text-base leading-relaxed text-bf-muted sm:text-lg">
            Not a quiz. Not a form. Just one warm, unhurried conversation that
            gently uncovers your real gifts, your quiet strengths, and everything
            you've already built — then reflects it back so you can build
            something of your own.
          </p>

          <div className="mt-11 flex flex-col items-start gap-4">
            <button
              data-testid="google-login-button"
              onClick={handleLogin}
              className="group inline-flex items-center gap-3 rounded-full bg-gradient-to-r from-bf-gold to-bf-rose px-7 py-3.5 font-sans text-sm font-medium text-[#2A1521] shadow-[0_0_40px_-8px_rgba(255,180,90,0.6)] transition-transform duration-300 hover:scale-[1.03]"
            >
              <GoogleGlyph />
              Continue with Google
            </button>
            <p className="font-sans text-xs text-bf-muted/70">
              Your reflection is saved privately to your account so you can return to it anytime.
            </p>
          </div>
        </motion.div>
      </div>

      <footer className="relative z-10 px-6 pb-8 text-center font-sans text-xs tracking-wide text-bf-muted/50">
        Warm, human, and yours. — Bliss Factory
      </footer>
    </div>
  );
}

const GoogleGlyph = () => (
  <svg className="h-4 w-4" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);
